import { Alert, AlertTitle, Button } from '@munifocus/ui';
import { useTranslation } from 'react-i18next';
import { Link as RouterLink } from 'react-router-dom';
import { apiErrorStatus } from '../lib/mutation-toast';

/** A 403 or 404 on a file: the API answers both for "not yours to see". */
export function isFileUnavailable(error: unknown): boolean {
  const status = apiErrorStatus(error);
  return status === 403 || status === 404;
}

/**
 * What a file screen shows when the file did not load.
 *
 * The API answers 404 for a file outside the officer's reach as well as for
 * one that does not exist — deliberately, so the answer does not confirm the
 * file is there. Both used to read "We couldn't load this file", which sounds
 * like an outage and invites a reload that can never help. That case now says
 * what it is and offers the way back; anything else (a 5xx, no connection)
 * keeps the load error.
 */
export function FileLoadError({ error }: { error: unknown }) {
  const { t } = useTranslation();
  if (!isFileUnavailable(error)) {
    return <Alert severity="error">{t('gov.file.loadError')}</Alert>;
  }
  return (
    <Alert
      severity="info"
      action={
        <Button component={RouterLink} to="/" size="small" color="inherit">
          {t('gov.file.backToFiles')}
        </Button>
      }
    >
      <AlertTitle>{t('gov.file.unavailable')}</AlertTitle>
      {t('gov.file.unavailableBody')}
    </Alert>
  );
}
